import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import useIsMobile from '../../hooks/useIsMobile';

/** Floating geometric shapes — Services page background.
 *  Cubes, spheres, tetrahedrons, torus rings in violet/cyan/magenta/orange.
 *  Slow rotation + gentle bob + cursor parallax. Frosted-glass material.
 */
const SHAPE_COLOURS = [0x7B61FF, 0x00E5CC, 0xFF3D8A, 0xFF8A3D];
const SHAPE_COUNT = 14;

const FloatingShapesBg = () => {
    const mountRef = useRef(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (isMobile) return;
        const mount = mountRef.current;
        if (!mount) return;
        const w = mount.clientWidth, h = mount.clientHeight;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 100);
        camera.position.z = 14;

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(w, h);
        renderer.setClearColor(0x000000, 0);
        mount.appendChild(renderer.domElement);

        scene.add(new THREE.AmbientLight(0xffffff, 1.1));
        const dir = new THREE.DirectionalLight(0xffffff, 0.6);
        dir.position.set(5, 10, 7);
        scene.add(dir);

        const geometries = [
            () => new THREE.BoxGeometry(1, 1, 1),
            () => new THREE.SphereGeometry(0.7, 24, 18),
            () => new THREE.TetrahedronGeometry(0.95),
            () => new THREE.TorusGeometry(0.7, 0.22, 16, 40),
            () => new THREE.OctahedronGeometry(0.85),
            () => new THREE.IcosahedronGeometry(0.8),
        ];

        const shapes = [];
        for (let i = 0; i < SHAPE_COUNT; i++) {
            const colour = SHAPE_COLOURS[i % SHAPE_COLOURS.length];
            const geom = geometries[i % geometries.length]();
            const mat = new THREE.MeshPhysicalMaterial({
                color: colour,
                transparent: true,
                opacity: 0.34,
                roughness: 0.25,
                metalness: 0.0,
                clearcoat: 1.0,
                clearcoatRoughness: 0.15,
                transmission: 0.55,
                thickness: 0.5,
            });
            const mesh = new THREE.Mesh(geom, mat);
            mesh.position.set(
                (Math.random() - 0.5) * 18,
                (Math.random() - 0.5) * 12,
                (Math.random() - 0.5) * 8
            );
            mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
            // Wireframe overlay for glow
            const wire = new THREE.LineSegments(
                new THREE.EdgesGeometry(geom),
                new THREE.LineBasicMaterial({
                    color: colour,
                    transparent: true,
                    opacity: 0.55,
                })
            );
            mesh.add(wire);
            mesh.userData = {
                spinX: (Math.random() - 0.5) * 0.4,
                spinY: (Math.random() - 0.5) * 0.4,
                bobAmp: 0.4 + Math.random() * 0.6,
                bobSpeed: 0.3 + Math.random() * 0.5,
                bobPhase: Math.random() * Math.PI * 2,
                baseY: mesh.position.y,
                anchorX: mesh.position.x,
            };
            shapes.push(mesh);
            scene.add(mesh);
        }

        // Mouse parallax (normalised -1..1)
        const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
        const onMouseMove = (e) => {
            const rect = mount.getBoundingClientRect();
            mouse.tx = (e.clientX - rect.left) / rect.width - 0.5;
            mouse.ty = (e.clientY - rect.top) / rect.height - 0.5;
        };
        window.addEventListener('mousemove', onMouseMove);

        const onResize = () => {
            if (!mount) return;
            const W = mount.clientWidth, H = mount.clientHeight;
            renderer.setSize(W, H);
            camera.aspect = W / H;
            camera.updateProjectionMatrix();
        };
        window.addEventListener('resize', onResize);

        const clock = new THREE.Clock();
        let raf = 0;
        const tick = () => {
            raf = requestAnimationFrame(tick);
            const t = clock.getElapsedTime();
            const dt = clock.getDelta();
            mouse.x += (mouse.tx - mouse.x) * 0.05;
            mouse.y += (mouse.ty - mouse.y) * 0.05;

            shapes.forEach((m) => {
                m.rotation.x += m.userData.spinX * dt;
                m.rotation.y += m.userData.spinY * dt;
                m.position.y =
                    m.userData.baseY +
                    Math.sin(t * m.userData.bobSpeed + m.userData.bobPhase) *
                        m.userData.bobAmp;
                m.position.x = m.userData.anchorX + mouse.x * 1.6;
            });
            camera.position.x = mouse.x * 1.5;
            camera.position.y = -mouse.y * 1.0;
            camera.lookAt(0, 0, 0);
            renderer.render(scene, camera);
        };
        tick();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('resize', onResize);
            if (mount && renderer.domElement.parentNode === mount) {
                mount.removeChild(renderer.domElement);
            }
            shapes.forEach((m) => {
                m.geometry.dispose();
                m.material.dispose();
            });
            renderer.dispose();
        };
    }, [isMobile]);

    if (isMobile) {
        return (
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        'radial-gradient(circle at 20% 30%, rgba(37,99,235,0.12), transparent 55%), radial-gradient(circle at 80% 70%, rgba(56,189,248,0.12), transparent 55%), radial-gradient(circle at 50% 50%, rgba(14,165,233,0.08), transparent 60%)',
                }}
                aria-hidden="true"
            />
        );
    }

    return (
        <div
            ref={mountRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            aria-hidden="true"
            data-testid="bg-floating-shapes"
        />
    );
};

export default FloatingShapesBg;
